import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Navigation, MapPin, Package, CheckCircle2, LogOut, Menu, Clock, 
  Play, Square, AlertCircle, Truck, Calendar, Timer, MapPinned,
  Coffee, Wrench, Phone, AlertTriangle, Pause, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '../../components/LanguageSelector';
import api from '../../utils/api';

const DriverDashboard = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Clock-in/out state
  const [clockStatus, setClockStatus] = useState({ is_clocked_in: false });
  const [showClockDialog, setShowClockDialog] = useState(false);
  const [clockNote, setClockNote] = useState('');
  const [clockingIn, setClockingIn] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  
  // Tracking state
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const [trackingPaused, setTrackingPaused] = useState(false);
  const [driverStatus, setDriverStatus] = useState('driving');
  
  // Status & Emergency dialogs
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEmergencyDialog, setShowEmergencyDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [emergencyType, setEmergencyType] = useState('breakdown');
  const [emergencyDescription, setEmergencyDescription] = useState('');
  const [sendingStatus, setSendingStatus] = useState(false);

  // Fetch jobs and clock status on mount
  useEffect(() => {
    fetchJobs();
    fetchClockStatus();
    fetchDriverStatus();
  }, []);

  // Dynamic tracking interval (from API response)
  const [nextUpdateInterval, setNextUpdateInterval] = useState(360000); // Default 6 minutes

  // Start/stop location tracking based on clock status
  useEffect(() => {
    let trackingInterval;
    
    if (clockStatus.is_clocked_in && trackingEnabled) {
      // Send location at dynamic interval (6 min normal, 15 min stationary)
      trackingInterval = setInterval(() => {
        sendLocationUpdate();
      }, nextUpdateInterval);
      
      // Send initial update
      sendLocationUpdate();
    }
    
    return () => {
      if (trackingInterval) clearInterval(trackingInterval);
    };
  }, [clockStatus.is_clocked_in, trackingEnabled, nextUpdateInterval]);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/jobs');
      setJobs(response.data || []);
      // Find active job
      const active = response.data.jobs?.find(j => 
        ['in_progress', 'en_route_pickup', 'loading', 'en_route_delivery'].includes(j.status)
      );
      setActiveJob(active);
    } catch (error) {
      console.log('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClockStatus = async () => {
    try {
      const response = await api.get('/time-tracking/status');
      setClockStatus(response.data);
      if (response.data.is_clocked_in) {
        setTrackingEnabled(true);
      }
    } catch (error) {
      console.log('Error fetching clock status:', error);
    }
  };

  const fetchDriverStatus = async () => {
    try {
      const response = await api.get('/tracking/driver-status');
      setDriverStatus(response.data.status || 'driving');
      setTrackingPaused(response.data.pause_tracking || false);
    } catch (error) {
      console.log('Error fetching driver status:', error);
    }
  };

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  // Update driver activity status (break, at destination, etc.)
  const updateDriverStatus = async (status) => {
    setSendingStatus(true);
    try {
      const position = await getCurrentPosition();
      
      const response = await api.post('/tracking/driver-status', {
        status: status,
        latitude: position.latitude,
        longitude: position.longitude,
        note: statusNote,
        job_id: activeJob?.id
      });
      
      setDriverStatus(status);
      setTrackingPaused(response.data.tracking_paused);
      setNextUpdateInterval(response.data.next_update_seconds * 1000);
      
      toast.success(`Status: ${status.replace(/_/g, ' ')}`);
      setShowStatusDialog(false);
      setStatusNote('');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSendingStatus(false);
    }
  };

  // Resume tracking after break
  const resumeTracking = async () => {
    setSendingStatus(true);
    try {
      const response = await api.post('/tracking/resume');
      setDriverStatus('driving');
      setTrackingPaused(false);
      setNextUpdateInterval(response.data.next_update_seconds * 1000);
      toast.success('Tracking resumed');
    } catch (error) {
      toast.error('Failed to resume tracking');
    } finally {
      setSendingStatus(false);
    }
  };

  // Send emergency alert
  const sendEmergencyAlert = async () => {
    setSendingStatus(true);
    try {
      const position = await getCurrentPosition();
      
      await api.post('/tracking/emergency-alert', {
        alert_type: emergencyType,
        description: emergencyDescription,
        latitude: position.latitude,
        longitude: position.longitude,
        job_id: activeJob?.id
      });
      
      setDriverStatus('emergency');
      setTrackingPaused(true);
      toast.success('Emergency alert sent! Help is on the way.', { duration: 5000 });
      setShowEmergencyDialog(false);
      setEmergencyDescription('');
    } catch (error) {
      toast.error('Failed to send emergency alert');
    } finally {
      setSendingStatus(false);
    }
  };

  const handleClockIn = async () => {
    setClockingIn(true);
    setLocationError(null);
    
    try {
      const position = await getCurrentPosition();
      setCurrentLocation(position);
      
      const response = await api.post('/time-tracking/clock-in', {
        latitude: position.latitude,
        longitude: position.longitude,
        note: clockNote,
        job_id: activeJob?.id,
        device_info: navigator.userAgent
      });
      
      toast.success('Clocked in successfully!');
      setClockStatus({ is_clocked_in: true, ...response.data });
      setTrackingEnabled(true);
      setShowClockDialog(false);
      setClockNote('');
    } catch (error) {
      if (error.message?.includes('Geolocation')) {
        setLocationError('Unable to get your location. Please enable GPS.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clock in');
      }
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingIn(true);
    setLocationError(null);
    
    try {
      const position = await getCurrentPosition();
      
      const response = await api.post('/time-tracking/clock-out', {
        latitude: position.latitude,
        longitude: position.longitude,
        note: clockNote,
        device_info: navigator.userAgent
      });
      
      toast.success(`Clocked out! Total: ${response.data.total_hours} hours`);
      setClockStatus({ is_clocked_in: false });
      setTrackingEnabled(false);
      setShowClockDialog(false);
      setClockNote('');
    } catch (error) {
      if (error.message?.includes('Geolocation')) {
        setLocationError('Unable to get your location. Please enable GPS.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clock out');
      }
    } finally {
      setClockingIn(false);
    }
  };

  const sendLocationUpdate = async () => {
    // Don't send if tracking is paused
    if (trackingPaused) {
      return;
    }
    
    try {
      const position = await getCurrentPosition();
      
      const response = await api.post('/tracking/location', {
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: position.accuracy,
        job_id: activeJob?.id
      });
      
      setLastLocationUpdate(new Date());
      
      // Update interval based on API response (6 min normal, 15 min stationary)
      if (response.data.next_update_seconds) {
        setNextUpdateInterval(response.data.next_update_seconds * 1000);
      }
      
      // Handle limit reached
      if (response.data.limit_reached || response.data.limit_exceeded) {
        toast.error(response.data.message);
        setTrackingEnabled(false);
      }
      
      // Prompt driver for status if stationary
      if (response.data.prompt_status_input && !showStatusDialog) {
        setShowStatusDialog(true);
      }
      
      // Handle geofence events (arrived at pickup/delivery)
      if (response.data.geofence_events?.length > 0) {
        response.data.geofence_events.forEach(event => {
          toast.success(`📍 ${event.type.replace(/_/g, ' ')}: ${event.location}`, { duration: 5000 });
        });
      }
    } catch (error) {
      console.log('Location update failed:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('movemate_token');
    localStorage.removeItem('movemate_user');
    setUser(null);
    navigate('/mobile-login');
  };

  const formatElapsedTime = (clockInTime) => {
    if (!clockInTime) return '0:00';
    const start = new Date(clockInTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000 / 60); // minutes
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const todaysJobs = jobs.filter(j => {
    const jobDate = new Date(j.moving_date);
    const today = new Date();
    return jobDate.toDateString() === today.toDateString();
  });

  const completedToday = todaysJobs.filter(j => j.status === 'completed').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="driver-dashboard">
      {/* Mobile Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="MoveMate" className="h-8" />
              <div>
                <h1 className="text-lg font-bold gradient-text">MoveMate</h1>
                <p className="text-xs text-gray-500">Driver</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Clock Status Indicator */}
              {clockStatus.is_clocked_in && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                  <Clock size={12} className="mr-1" />
                  {formatElapsedTime(clockStatus.clock_in_time)}
                </Badge>
              )}
              <button onClick={() => setMenuOpen(!menuOpen)} className="p-2">
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t bg-white">
            <div className="p-4 space-y-2">
              <div className="mb-4">
                <p className="text-sm font-semibold">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-gray-500">Driver</p>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Language</span>
                <LanguageSelector variant="compact" />
              </div>
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="p-4 space-y-6">
        {/* Clock In/Out Card */}
        <Card className={`${clockStatus.is_clocked_in ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-gray-500 to-gray-600'} text-white`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm opacity-90">Work Status</p>
                <h3 className="text-2xl font-bold">
                  {clockStatus.is_clocked_in ? 'Clocked In' : 'Clocked Out'}
                </h3>
                {clockStatus.is_clocked_in && clockStatus.clock_in_time && (
                  <p className="text-sm opacity-80">
                    Since {new Date(clockStatus.clock_in_time).toLocaleTimeString()}
                  </p>
                )}
              </div>
              <div className="p-4 bg-white/20 rounded-full">
                {clockStatus.is_clocked_in ? (
                  <Timer size={32} />
                ) : (
                  <Clock size={32} />
                )}
              </div>
            </div>
            
            <Button 
              className={`w-full ${clockStatus.is_clocked_in ? 'bg-white text-red-600 hover:bg-gray-100' : 'bg-white text-green-600 hover:bg-gray-100'}`}
              size="lg"
              onClick={() => setShowClockDialog(true)}
              data-testid="clock-btn"
            >
              {clockStatus.is_clocked_in ? (
                <>
                  <Square size={20} className="mr-2" />
                  Clock Out
                </>
              ) : (
                <>
                  <Play size={20} className="mr-2" />
                  Clock In
                </>
              )}
            </Button>
            
            {clockStatus.is_clocked_in && (
              <>
                <p className="text-xs text-center mt-3 opacity-80">
                  <MapPinned size={12} className="inline mr-1" />
                  {trackingPaused 
                    ? `Tracking paused (${driverStatus.replace(/_/g, ' ')})`
                    : 'Location tracking active (6 min intervals)'
                  }
                </p>
                
                {/* Status & Emergency Buttons */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {trackingPaused ? (
                    <Button 
                      variant="outline" 
                      className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                      onClick={resumeTracking}
                      disabled={sendingStatus}
                      data-testid="resume-btn"
                    >
                      <RotateCcw size={16} className="mr-2" />
                      Resume
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                      onClick={() => setShowStatusDialog(true)}
                      data-testid="status-btn"
                    >
                      <Pause size={16} className="mr-2" />
                      Status
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className="bg-red-500/80 border-red-400 text-white hover:bg-red-600"
                    onClick={() => setShowEmergencyDialog(true)}
                    data-testid="emergency-btn"
                  >
                    <AlertTriangle size={16} className="mr-2" />
                    Emergency
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Status Banner when paused */}
        {clockStatus.is_clocked_in && trackingPaused && driverStatus !== 'emergency' && (
          <Card className="bg-yellow-100 border-yellow-300">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Pause className="text-yellow-600" size={20} />
                  <span className="font-medium text-yellow-800">
                    {driverStatus === 'on_break' && 'On Break'}
                    {driverStatus === 'at_pickup' && 'At Pickup Location'}
                    {driverStatus === 'at_delivery' && 'At Delivery Location'}
                    {driverStatus === 'loading' && 'Loading'}
                    {driverStatus === 'unloading' && 'Unloading'}
                    {driverStatus === 'waiting' && 'Waiting'}
                  </span>
                </div>
                <Button size="sm" onClick={resumeTracking} disabled={sendingStatus}>
                  Resume Driving
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Emergency Banner */}
        {driverStatus === 'emergency' && (
          <Card className="bg-red-100 border-red-300">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="text-red-600" size={24} />
                <div>
                  <p className="font-bold text-red-800">Emergency Alert Active</p>
                  <p className="text-sm text-red-600">Admin has been notified. Help is on the way.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Job */}
        {!activeJob ? (
          <Card className="bg-gradient-to-br from-primary to-secondary text-white">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Package size={48} className="mx-auto mb-4 opacity-80" />
                <h3 className="text-xl font-bold mb-2">No Active Job</h3>
                <p className="text-sm opacity-90">Waiting for job assignment</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-gradient-to-br from-primary to-secondary text-white">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm opacity-90">Active Job</p>
                  <h3 className="text-2xl font-bold">{activeJob.reference || 'JOB'}</h3>
                </div>
                <Badge className="bg-white/20 text-white">
                  {activeJob.status?.replace(/_/g, ' ')}
                </Badge>
              </div>
              
              <div className="space-y-2 mb-4 text-sm">
                <p className="flex items-center gap-2">
                  <MapPin size={16} className="text-green-300" />
                  {activeJob.from_address?.formatted_address || 'Pickup location'}
                </p>
                <p className="flex items-center gap-2">
                  <MapPin size={16} className="text-red-300" />
                  {activeJob.to_address?.formatted_address || 'Delivery location'}
                </p>
              </div>
              
              <Button className="w-full bg-white text-primary hover:bg-gray-100" size="lg">
                <Navigation size={20} className="mr-2" />
                Start Navigation
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Today&apos;s Jobs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar size={20} />
              Today&apos;s Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todaysJobs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package size={48} className="mx-auto mb-2 text-gray-400" />
                <p>No jobs scheduled</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todaysJobs.map((job) => (
                  <div key={job.id} className="p-3 border rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{job.reference}</p>
                      <p className="text-xs text-gray-500">{job.from_address?.formatted_address?.slice(0, 30)}...</p>
                    </div>
                    <Badge variant={job.status === 'completed' ? 'success' : 'outline'}>
                      {job.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-2xl font-bold text-primary">{completedToday}</p>
              <p className="text-xs text-gray-600">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-2xl font-bold text-primary">{todaysJobs.length}</p>
              <p className="text-xs text-gray-600">Scheduled</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-2xl font-bold text-primary">
                {clockStatus.elapsed_hours?.toFixed(1) || '0'}
              </p>
              <p className="text-xs text-gray-600">Hours</p>
            </CardContent>
          </Card>
        </div>

        {/* Navigation Tips */}
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-2 text-green-900 flex items-center gap-2">
              <Truck size={20} />
              Driver Tips
            </h3>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• Clock in when you start your shift</li>
              <li>• Set status when stopped (break, at pickup/delivery)</li>
              <li>• Use Emergency button for breakdowns or incidents</li>
              <li>• Remember to clock out at the end of your shift</li>
            </ul>
          </CardContent>
        </Card>
      </main>

      {/* Clock In/Out Dialog */}
      <Dialog open={showClockDialog} onOpenChange={setShowClockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {clockStatus.is_clocked_in ? 'Clock Out' : 'Clock In'}
            </DialogTitle>
            <DialogDescription>
              {clockStatus.is_clocked_in 
                ? 'End your shift and stop location tracking'
                : 'Start your shift - GPS location will be recorded'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {locationError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="text-red-500" size={20} />
                <p className="text-sm text-red-700">{locationError}</p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Note (optional)</label>
              <Textarea
                placeholder={clockStatus.is_clocked_in 
                  ? "Any notes about your shift..."
                  : "Where are you starting from?"
                }
                value={clockNote}
                onChange={(e) => setClockNote(e.target.value)}
                rows={3}
              />
            </div>
            
            {clockStatus.is_clocked_in && clockStatus.clock_in_time && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Shift duration:</strong> {formatElapsedTime(clockStatus.clock_in_time)}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClockDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={clockStatus.is_clocked_in ? handleClockOut : handleClockIn}
              disabled={clockingIn}
              className={clockStatus.is_clocked_in ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
            >
              {clockingIn ? 'Getting Location...' : (clockStatus.is_clocked_in ? 'Clock Out' : 'Clock In')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Your Status</DialogTitle>
            <DialogDescription>
              Let us know what you&apos;re doing. This will pause location tracking until you resume.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant={selectedStatus === 'on_break' ? 'default' : 'outline'}
                className="h-20 flex flex-col items-center gap-2"
                onClick={() => setSelectedStatus('on_break')}
              >
                <Coffee size={24} />
                <span>On Break</span>
              </Button>
              <Button 
                variant={selectedStatus === 'at_pickup' ? 'default' : 'outline'}
                className="h-20 flex flex-col items-center gap-2"
                onClick={() => setSelectedStatus('at_pickup')}
              >
                <MapPin size={24} className="text-green-600" />
                <span>At Pickup</span>
              </Button>
              <Button 
                variant={selectedStatus === 'at_delivery' ? 'default' : 'outline'}
                className="h-20 flex flex-col items-center gap-2"
                onClick={() => setSelectedStatus('at_delivery')}
              >
                <MapPin size={24} className="text-red-600" />
                <span>At Delivery</span>
              </Button>
              <Button 
                variant={selectedStatus === 'loading' ? 'default' : 'outline'}
                className="h-20 flex flex-col items-center gap-2"
                onClick={() => setSelectedStatus('loading')}
              >
                <Package size={24} />
                <span>Loading</span>
              </Button>
              <Button 
                variant={selectedStatus === 'unloading' ? 'default' : 'outline'}
                className="h-20 flex flex-col items-center gap-2"
                onClick={() => setSelectedStatus('unloading')}
              >
                <Package size={24} />
                <span>Unloading</span>
              </Button>
              <Button 
                variant={selectedStatus === 'waiting' ? 'default' : 'outline'}
                className="h-20 flex flex-col items-center gap-2"
                onClick={() => setSelectedStatus('waiting')}
              >
                <Clock size={24} />
                <span>Waiting</span>
              </Button>
            </div>
            
            <div>
              <label className="text-sm font-medium">Note (optional)</label>
              <Textarea
                placeholder="Any additional details..."
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowStatusDialog(false);
              setSelectedStatus('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => updateDriverStatus(selectedStatus)}
              disabled={!selectedStatus || sendingStatus}
            >
              {sendingStatus ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency Alert Dialog */}
      <Dialog open={showEmergencyDialog} onOpenChange={setShowEmergencyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={24} />
              Emergency Alert
            </DialogTitle>
            <DialogDescription>
              Send an alert to your company admin with your location. They will be notified immediately.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Type of Emergency</label>
              <Select value={emergencyType} onValueChange={setEmergencyType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakdown">
                    <div className="flex items-center gap-2">
                      <Wrench size={16} /> Vehicle Breakdown
                    </div>
                  </SelectItem>
                  <SelectItem value="accident">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} /> Accident
                    </div>
                  </SelectItem>
                  <SelectItem value="flat_tire">
                    <div className="flex items-center gap-2">
                      <Truck size={16} /> Flat Tire
                    </div>
                  </SelectItem>
                  <SelectItem value="medical">
                    <div className="flex items-center gap-2">
                      <Phone size={16} /> Medical Emergency
                    </div>
                  </SelectItem>
                  <SelectItem value="security">
                    <div className="flex items-center gap-2">
                      <AlertCircle size={16} /> Security Issue
                    </div>
                  </SelectItem>
                  <SelectItem value="other">
                    <div className="flex items-center gap-2">
                      <AlertCircle size={16} /> Other
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Describe the situation..."
                value={emergencyDescription}
                onChange={(e) => setEmergencyDescription(e.target.value)}
                rows={3}
              />
            </div>
            
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Note:</strong> Your current GPS location will be sent with this alert. 
                Admin will receive an immediate notification.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmergencyDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendEmergencyAlert}
              disabled={sendingStatus}
              className="bg-red-600 hover:bg-red-700"
            >
              {sendingStatus ? 'Sending...' : 'Send Emergency Alert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDashboard;
